import { initializeApp } from "firebase/app";
import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  // Import for changeEmail function:
  updateEmail,
  sendEmailVerification,
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup
} from "firebase/auth";
import { axiosInstance } from "../api/AxiosInstance";
import { env } from "../env";
import { getBrowserLocaleDefaults, getBrowserTimezone } from "../utils/browserLocale";

const firebaseConfig = {
  apiKey: env.REACT_APP_FIREBASE_APIKEY,
  authDomain: env.REACT_APP_FIREBASE_AUTHDOMAIN,
  projectId: env.REACT_APP_FIREBASE_PROJECTID,
  storageBucket: env.REACT_APP_FIREBASE_STORAGEBUCKET,
  messagingSenderId: env.REACT_APP_FIREBASE_MESSAGINGSENDERID,
  appId: env.REACT_APP_FIREBASE_APPID,
  measurementId: env.REACT_APP_FIREBASE_MEASUREMENTID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

const getCurrentHostname = () => {
  if (typeof window === "undefined") {
    return null;
  }
  return window.location.hostname;
};

/**
 * Signs in with Google (popup) and optionally passes an invitation token.
 */
const signInWithGoogle = async (invitationToken = null) => {
  try {
    const res = await signInWithPopup(auth, googleProvider);
    const user = res.user;
    const { country, language } = getBrowserLocaleDefaults();

    const payload = {
      id: user.uid,
      firebase_id: user.uid,
      display_name: user.displayName,
      auth_provider: "google",
      email: user.email,
      country: country,
      language: language,
      timezone: getBrowserTimezone(),
      signup_domain: getCurrentHostname()
    };

    const queryParams = invitationToken
      ? `?invitation_token=${encodeURIComponent(invitationToken)}`
      : "";

    await axiosInstance.post(`/users/signup${queryParams}`, payload);
  } catch (err) {
    console.error(err);
  }
};

/**
 * Completes sign-in when using email link (magic link).
 * Prepares a payload and sends to backend to create/verify the user.
 */
const signInWithEmailLinkInternal = async (user) => {
  try {
    // Get the email from the URL query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get("emailForSignIn");

    if (!email) {
      throw new Error("No email found in URL query parameters for sign-in.");
    }

    const { country, language } = getBrowserLocaleDefaults();

    // Extract display name from email before "@"
    const displayNameFromEmail = email.split("@")[0];

    // Prepare the payload for signup
    const payload = {
      id: user.uid,
      firebase_id: user.uid,
      display_name: displayNameFromEmail || "Anonymous",
      auth_provider: "local",
      email: user.email,
      country: country,
      language: language,
      timezone: getBrowserTimezone(),
      signup_domain: getCurrentHostname(),
    };

    // Send the payload to the server
    await axiosInstance.post("/users/signup", payload);
    console.log("User signup payload successfully sent.");
  } catch (error) {
    console.error("An error occurred during sign-in or signup:", error);
  }
};

/**
 * Logs in using email & password.
 */
const logInWithEmailAndPassword = async (email, password) => {
  try {
    await signInWithEmailAndPassword(auth, email, password);
  } catch (err) {
    console.error(err);
    throw err;
  }
};

/**
 * Registers a new user with email & password and also sends signup info to your backend.
 */
const registerWithEmailAndPassword = async (
  display_name,
  email,
  password,
  country,
  language,
  timezone,
  invitation_token,
  cf_turnstile_response
) => {
  try {
    const res = await createUserWithEmailAndPassword(auth, email, password);
    const user = res.user;

    const queryParams = invitation_token
      ? `?invitation_token=${encodeURIComponent(invitation_token)}`
      : "";

    const payload = {
      id: user.uid,
      display_name: display_name,
      auth_provider: "local",
      email: email,
      country: country,
      language: language,
      timezone: timezone,
      signup_domain: getCurrentHostname(),
      ...(cf_turnstile_response ? { cf_turnstile_response } : {})
    };

    await axiosInstance.post(`/users/signup${queryParams}`, payload);
  } catch (err) {
    console.error(err);
    throw err;
  }
};

/**
 * Sends a password reset email.
 */
const sendPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (err) {
    console.error(err);
    throw err;
  }
};

/**
 * Logs out the user and redirects to /login.
 */
const logout = () => {
  signOut(auth);
  window.location.replace("/login");
};

/**
 * Changes the currently signed-in user's email address.
 * 1. Re-authenticates to ensure security (Firebase requires recent login).
 * 2. Updates the email in Firebase Auth.
 * 3. Optionally sends a verification email to the new address.
 * 4. Updates your backend to keep email in sync.
 *
 * @param {string} newEmail            The new email address
 * @param {string} currentPassword     The current password (if using email/password login)
 * @param {boolean} resendVerification Whether to send a new verification email (default: true)
 */
const changeEmail = async (newEmail, currentPassword, resendVerification = true) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("No user is currently signed in.");
  }

  // Identify if user is password-based or Google-based (other providers can be added similarly).
  const isPasswordProvider = user.providerData.some((p) => p.providerId === "password");
  const isGoogleProvider = user.providerData.some((p) => p.providerId === "google.com");

  try {
    // 1) Reauthenticate
    if (isPasswordProvider) {
      if (!currentPassword) {
        throw new Error("Current password is required to change email for email/password accounts.");
      }
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
    } else if (isGoogleProvider) {
      await reauthenticateWithPopup(user, googleProvider);
    } else {
      throw new Error("Changing email is not supported for this auth provider.");
    }

    // 2) Update in Firebase Auth
    await updateEmail(user, newEmail);

    // 3) Optionally send a verification email
    if (resendVerification) {
      try {
        await sendEmailVerification(user);
      } catch (err) {
        console.warn("Failed to send verification email:", err);
      }
    }

    console.log("Email changed successfully!");
  } catch (error) {
    console.error("Error changing email:", error);
    throw error;
  }
};

export {
  app,
  auth,
  signInWithGoogle,
  signInWithEmailLinkInternal,
  logInWithEmailAndPassword,
  registerWithEmailAndPassword,
  sendPasswordReset,
  logout,
  changeEmail
};
